import React, { useState } from 'react';

import Button from '../../../components/ui/Button';
import { format, addDays, startOfWeek, isSameDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const BookingCalendar = ({ courts, bookings }) => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('week'); // week, day

  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 6; hour <= 23; hour++) {
      slots?.push(`${hour?.toString()?.padStart(2, '0')}:00`);
    }
    return slots;
  };

  const generateWeekDays = () => {
    const start = startOfWeek(selectedDate, { weekStartsOn: 1 });
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
  };

  const getBookingForSlot = (courtId, date, time) => {
    return bookings?.find((booking) => {
      const bookingDate = new Date(booking?.date);
      const bookingTime = booking?.time?.split(' - ')?.[0];
      return (booking?.courtName?.includes(courtId?.toString()) &&
      isSameDay(bookingDate, date) && bookingTime === time);
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'bg-success/10 border-success text-success';
      case 'pending_approval':
        return 'bg-warning/10 border-warning text-warning';
      case 'cancelled':
        return 'bg-destructive/10 border-destructive text-destructive';
      default:
        return 'bg-muted/50 border-muted text-muted-foreground';
    }
  };

  const timeSlots = generateTimeSlots();
  const weekDays = generateWeekDays();

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-2 p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-heading font-bold text-card-foreground mb-2">
            Calendário de Reservas
          </h3>
          <p className="text-sm text-muted-foreground">
            Visualize e gerencie as reservas em tempo real
          </p>
        </div>

        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <Button
              variant={viewMode === 'week' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('week')}
            >
              Semana
            </Button>
            <Button
              variant={viewMode === 'day' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('day')}
            >
              Dia
            </Button>
          </div>

          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedDate(addDays(selectedDate, -7))}
              iconName="ChevronLeft"
              iconSize={16}
            />
            <span className="text-sm font-medium text-card-foreground px-3">
              {format(selectedDate, "MMMM 'de' yyyy", { locale: ptBR })}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedDate(addDays(selectedDate, 7))}
              iconName="ChevronRight"
              iconSize={16}
            />
          </div>
        </div>
      </div>
      {/* Legend */}
      <div className="flex items-center space-x-4 mb-4 p-3 bg-muted/30 rounded-lg">
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 rounded bg-success"></div>
          <span className="text-xs text-muted-foreground">Confirmado</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 rounded bg-warning"></div>
          <span className="text-xs text-muted-foreground">Pendente</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 rounded bg-destructive"></div>
          <span className="text-xs text-muted-foreground">Cancelado</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 rounded bg-muted"></div>
          <span className="text-xs text-muted-foreground">Disponível</span>
        </div>
      </div>
      {/* Calendar Grid */}
      <div className="overflow-x-auto">
        <div className="min-w-full">
          {/* Header */}
          <div className="grid grid-cols-8 gap-1 mb-2">
            <div className="p-2 text-xs font-medium text-muted-foreground">
              Horário
            </div>
            {weekDays?.map((day, index) => (
              <div key={index} className="p-2 text-center">
                <div className="text-xs font-medium text-muted-foreground">
                  {format(day, 'EEE', { locale: ptBR })}
                </div>
                <div className="text-sm font-bold text-card-foreground">
                  {format(day, 'd')}
                </div>
              </div>
            ))}
          </div>

          {/* Time Slots */}
          <div className="space-y-1">
            {timeSlots?.map((time) => (
              <div key={time} className="grid grid-cols-8 gap-1">
                <div className="p-2 text-xs font-medium text-muted-foreground bg-muted/20 rounded">
                  {time}
                </div>
                {weekDays?.map((day, dayIndex) => (
                  <div key={dayIndex} className="min-h-[60px] space-y-1">
                    {courts?.map((court) => {
                      const booking = getBookingForSlot(court?.id, day, time);
                      return (
                        <div
                          key={court?.id}
                          className={`p-2 rounded text-xs border transition-colors cursor-pointer hover:opacity-80 ${
                            booking
                              ? getStatusColor(booking?.status)
                              : 'bg-muted/10 border-muted/30 hover:bg-muted/20'
                          }`}
                          title={
                            booking
                              ? `${booking?.customerName} - ${court?.name} - R$ ${booking?.amount?.toFixed(2)}`
                              : `${court?.name} - Disponível`
                          }
                        >
                          {booking ? (
                            <div className="space-y-1">
                              <div className="font-medium truncate">
                                {booking?.customerName}
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="truncate">{court?.name}</span>
                                <span className="font-medium">
                                  R$ {booking?.amount?.toFixed(0)}
                                </span>
                              </div>
                            </div>
                          ) : (
                            <div className="text-center opacity-60">
                              <div className="text-xs truncate">{court?.name}</div>
                              <div className="text-xs">Disponível</div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Quick Actions */}
      <div className="mt-6 flex items-center justify-between pt-4 border-t border-border">
        <div className="text-sm text-muted-foreground">
          {bookings?.length || 0} reservas encontradas
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="outline" size="sm" iconName="Download" iconSize={16}>
            Exportar
          </Button>
          <Button variant="default" size="sm" iconName="Plus" iconSize={16}>
            Nova Reserva
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BookingCalendar;